import { useCallback, useEffect, useState } from 'react';
import {
  NavLink,
  generatePath,
  useParams,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  IconArrowLeft,
  PermissionEnum,
  Response,
  ResponseError,
  getResponseErrors,
  Popup,
  InputText,
  Checkbox,
  responseErrorsResolver,
  IconLoader,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import GeneralInfo from '../../components/bank-and-psp/account-general-info';
import ChangeLog from '../../components/bank-and-psp/accounts-logs';
import Documents from '../../components/bank-and-psp/documents';
import InternalAccounts from '../../components/bank-and-psp/internal-accounts';
import {
  clearBankAndPspAccountPageState,
  setBalances,
} from './store/slices/account';
import {
  useLazyGetPspAccountQuery,
  useLazyGetPspAccountsLogsQuery,
  useLazyGetPspPartnerQuery,
  useLazyGetPspInternalAccountsQuery,
  useLazyGetConfigurationAccountBalanceQuery,
  useUpdatePspAccountMutation,
  useEnablePspAccountMutation,
  useLazyGetPspAccountDocumentsQuery,
  useLazyGetBankCASPQuery,
  useLazyGetPspAccountsCategoriesQuery,
  useBindPSPExternalAccountMutation,
  useGenerateNewPSPExternalAccountMutation,
  useLazyGetConfigurationPspAccountsQuery,
} from './store/services';

import { BankRoutes } from '../../routes';
import { Balance, PspAccountItem } from './types';

import cn from './bank-and-psp.module.css';

const AccountPage = () => {
  const {
    bankAndPsp: {
      accountPage: {
        generalInfo,
        documents,
        changeLog,
        internalAccounts,
        balances,
      },
      info: {
        generalInfo: pspGeneralInfo,
        casp,
        pspAccountsCategories,
        takeCount,
      },
    },
    configurationDictionaries: {
      countries,
      activePermissions,
      overdraftEnabled,
    },
  } = useAppSelector((state) => state);
  const [currentBalances, setCurrentBanalces] = useState<Balance[] | undefined>(
    undefined,
  );
  const [vaultSetting, setVaultSetting] = useState<'bind' | 'create-new'>(
    'bind',
  );
  const [ibanValue, setIbanValue] = useState('');
  const [generalBindError, setGeneralBindError] = useState('');
  const [createNewHintVisible, setCreateNewHintIsVisible] = useState(false);
  const [visibileByClientsPopupIsVisible, setVisibleByClientsPopupIsVisible] =
    useState(false);
  const [excludeForExcternalPopupVisible, setExcludeForExcternalPopupVisible] =
    useState(false);
  const [useAsDefaultPopupVsibile, setUseAsDefaultPopupVisible] =
    useState(false);

  const { accountId } = useParams();
  const dispatch = useAppDispatch();

  const {
    setError,
    formState: { errors },
  } = useForm<any>();

  const isDirty = generalInfo?.realNumberOrIban !== ibanValue;

  const [getAccount, { isLoading, isFetching }] = useLazyGetPspAccountQuery();
  const [getLogs, { isLoading: logsIsLoading, isFetching: logsIsFetching }] =
    useLazyGetPspAccountsLogsQuery();
  const [getPartner] = useLazyGetPspPartnerQuery();
  const [getBankCasp] = useLazyGetBankCASPQuery();
  const [bindSidepanelIsOpen, setBindSidepanelIsOpen] = useState(false);
  const [
    getInternalAccounts,
    {
      isLoading: internalAccountsIsLoading,
      isFetching: internalAccountsIsFetching,
    },
  ] = useLazyGetPspInternalAccountsQuery();
  const [getConfigurationAccountBalance] =
    useLazyGetConfigurationAccountBalanceQuery();
  const [updateAccount, { isLoading: visiblityChangeInProgress }] =
    useUpdatePspAccountMutation();
  const [
    changeDefaultState,
    {
      isLoading: defaultStateChangeInProgress,
      isSuccess: changeDefaultStateIsSuccess,
    },
  ] = useUpdatePspAccountMutation();
  const [
    getDocuments,
    { isLoading: documentsIsLoading, isFetching: documentsIsFetching },
  ] = useLazyGetPspAccountDocumentsQuery();
  const [getPspAccountsCategories] = useLazyGetPspAccountsCategoriesQuery();
  const [updateAccountIban, { isLoading: updateIbanInProgress }] =
    useUpdatePspAccountMutation();
  const [bind, { isLoading: bindInProgress, isSuccess: bindIsSuccess }] =
    useBindPSPExternalAccountMutation();
  const [
    createNew,
    { isLoading: createNewInProgress, isSuccess: createNewIsSuccess },
  ] = useGenerateNewPSPExternalAccountMutation();
  const [getAccounts] = useLazyGetConfigurationPspAccountsQuery();

  const [logsErrors, setLogsErrors] = useState('');
  const [internalAccountsErrors, setInternalAccountsErrors] = useState('');
  const [errorList, setErrorList] = useState('');
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);

  const { search } = useLocation();

  const getAccountAction = useCallback(async () => {
    const response = (await getAccount(accountId as string)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      getResponseErrors({ error: response.error, setErrorList });
    }
  }, [accountId]);

  const getInternalAccountBalance = useCallback(async () => {
    const response = (await getConfigurationAccountBalance(
      generalInfo?.account?.id as string,
    )) as { data: Response<Balance> };

    if (response.data.data) {
      setCurrentBanalces(response.data.data?.edges?.map((item) => item.node));
    }
  }, [generalInfo]);

  const getBalancesAction = useCallback(
    async (accountsList?: PspAccountItem[]) => {
      if (accountsList && accountsList.length) {
        const balancesResponse = await Promise.all(
          accountsList.map(async (item) => {
            const response = await getConfigurationAccountBalance(
              item.account.id,
            );

            let result = null;

            if (response?.data?.data) {
              result = response?.data?.data?.edges;
            }

            return result;
          }),
        );

        dispatch(
          setBalances(
            balancesResponse?.flat()?.map((item) => item?.node) as Balance[],
          ),
        );
      }
    },
    [],
  );

  const getInternalAccountsAction = useCallback(async () => {
    setInternalAccountsErrors('');
    const response = (await getInternalAccounts(accountId as string)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      if (response?.error?.data?.error) {
        if (
          typeof response?.error?.data?.error === 'string' &&
          !response?.error?.data?.message
        ) {
          setInternalAccountsErrors(response?.error?.data?.error);
        } else if (
          response?.error?.data?.message &&
          typeof response?.error?.data?.message === 'string'
        ) {
          setInternalAccountsErrors(response?.error?.data?.message);
        } else if (
          response?.error?.data?.message &&
          Array.isArray(response?.error?.data?.message)
        ) {
          const messages: string[] = [];

          response?.error?.data?.message?.forEach((item) => {
            messages.push(item.value as string);
          });

          setInternalAccountsErrors(messages?.join(', '));
        } else {
          setInternalAccountsErrors('Internal Server Error. Try again.');
        }
      } else if (response?.error?.status === 500) {
        setInternalAccountsErrors('Internal Server Error. Try again.');
      }
    }
  }, [accountId]);

  const getLogsAction = useCallback(async () => {
    setLogsErrors('');
    const response = await getLogs(`pspAccountId=${accountId}`);

    if (response?.error) {
      setLogsErrors('Internal Server Error, Try Again');
    }
  }, [accountId]);

  const getPspAccountDocuments = useCallback(() => {
    getDocuments(accountId as string);
  }, [accountId]);

  const getPartnerAction = useCallback(() => {
    getPartner(generalInfo?.pspPartnerId as string);
  }, [generalInfo?.pspPartnerId]);

  const [enable, { isLoading: enableInProgress }] =
    useEnablePspAccountMutation();

  const onStatusChange = async (status: boolean) => {
    await enable({ id: accountId as string, active: status });
  };

  const changeVisibility = async () => {
    if (accountId) {
      const response = await updateAccount({
        body: { visibleToClient: !generalInfo?.visibleToClient },
        id: accountId,
      });

      if (!response.error) {
        setVisibleByClientsPopupIsVisible(false);
      }
    }
  };

  const changeDefaultStateAction = async () => {
    if (accountId) {
      const response = await changeDefaultState({
        body: {
          isDefaultForExternalOperations:
            !generalInfo?.isDefaultForExternalOperations,
        },
        id: accountId,
      });

      if (!response.error) {
        setUseAsDefaultPopupVisible(false);
      }
    }
  };

  const changeExclude = async () => {
    if (accountId) {
      const response = await updateAccount({
        body: {
          excludeFromTopUpIfExternalAccountAvailable:
            !generalInfo?.excludeFromTopUpIfExternalAccountAvailable,
        },
        id: accountId,
      });

      if (!response.error) {
        setExcludeForExcternalPopupVisible(false);
      }
    }
  };

  const onLink = async () => {
    setGeneralBindError('');

    let response;

    if (vaultSetting === 'bind') {
      const updateResponse = await updateAccountIban({
        id: generalInfo?.id as string,
        body: {
          realNumberOrIban: ibanValue,
        },
      });

      if (updateResponse) {
        if (!updateResponse.error) {
          response = await bind({
            pspAccountId: generalInfo?.id as string,
          });
        } else {
          responseErrorsResolver({
            errors: updateResponse?.error as ResponseError,
            setError: () => {},
            setGlobalError: setGeneralBindError,
          });
        }
      }
    } else if (vaultSetting === 'create-new') {
      response = await createNew({
        pspAccountId: generalInfo?.id as string,
      });
    }

    if (response) {
      if (response?.error) {
        responseErrorsResolver({
          errors: response?.error as ResponseError,
          setError,
          setGlobalError: setGeneralBindError,
        });
      }
    }
  };

  useEffect(() => {
    if (changeDefaultStateIsSuccess && generalInfo) {
      getAccounts({
        id: generalInfo?.pspPartnerId,
        query: `?take=${takeCount || '20'}`,
      });
    }
  }, [changeDefaultStateIsSuccess]);

  useEffect(() => {
    if (internalAccounts && internalAccounts.length) {
      getBalancesAction(internalAccounts);
    }
  }, [internalAccounts, getBalancesAction]);

  useEffect(() => {
    if (generalInfo && !pspGeneralInfo) {
      getPartnerAction();
    }
  }, [getPartnerAction, generalInfo, pspGeneralInfo]);

  useEffect(() => {
    if (generalInfo && !internalAccounts) {
      getInternalAccountsAction();
    }
  }, [getInternalAccountsAction, generalInfo, internalAccounts]);

  useEffect(() => {
    if (accountId && !generalInfo) {
      getAccountAction();
    }
  }, [generalInfo, accountId]);

  useEffect(() => {
    if (generalInfo && generalInfo?.account) {
      getInternalAccountBalance();
    }
  }, [generalInfo]);

  useEffect(() => {
    if (!casp) {
      getBankCasp();
    }
  }, [casp]);

  useEffect(() => {
    if (
      accountId &&
      !documents &&
      activePermissions?.includes(PermissionEnum.PSP_ACCOUNT_DOCUMENTS_LIST)
    ) {
      getPspAccountDocuments();
    }
  }, [accountId, documents, activePermissions]);

  useEffect(() => {
    if (
      !pspAccountsCategories &&
      activePermissions?.includes(PermissionEnum.PSP_LIST)
    ) {
      getPspAccountsCategories();
    }
  }, [pspAccountsCategories, activePermissions]);

  useEffect(() => {
    if (accountId && !changeLog) {
      getLogsAction();
    }
  }, [accountId, getLogsAction, changeLog]);

  useEffect(() => {
    if (generalInfo) {
      setIbanValue(generalInfo?.realNumberOrIban as string);
    }
  }, [generalInfo]);

  useEffect(() => {
    if (bindIsSuccess) {
      setBindSidepanelIsOpen(false);
    }

    if (createNewIsSuccess) {
      setBindSidepanelIsOpen(false);
      setVaultSetting('bind');

      setCreateNewHintIsVisible(true);

      setTimeout(() => {
        setCreateNewHintIsVisible(false);
        getAccountAction();
      }, 8000);
    }
  }, [bindIsSuccess, createNewIsSuccess]);

  useEffect(() => {
    let title =
      pspGeneralInfo?.companyName && generalInfo?.account.fullNumber
        ? `Bank: ${pspGeneralInfo.companyName} - Account No: ${generalInfo?.account.fullNumber}`
        : 'Bank';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [pspGeneralInfo?.companyName, generalInfo?.account.fullNumber]);

  let linkButtonText;

  if (updateIbanInProgress || bindInProgress || createNewInProgress) {
    linkButtonText = 'Loading...';
  } else {
    linkButtonText =
      vaultSetting === 'bind'
        ? `Link ${
            pspGeneralInfo?.pspIntegration?.name === 'PRIME_VAULT_WALLET'
              ? 'Vault'
              : 'External Account'
          }`
        : 'Create new';
  }

  return (
    <div className={cn.bankAndPspPage}>
      <div className={cn.bankAndPspPageContent}>
        <div className={cn.bankAndPspPageLinkWrapper}>
          <NavLink
            to={`${generatePath(BankRoutes.BANK_AND_PSP_ACCOUNTS, {
              bankId: generalInfo?.pspPartnerId
                ? generalInfo?.pspPartnerId
                : '',
            })}${search || ''}`}
            className={cn.bankAndPspPageBackLink}
            onClick={() => dispatch(clearBankAndPspAccountPageState())}
          >
            <IconArrowLeft width={13} height={11} />
            Back
          </NavLink>
        </div>
        {errorList && (
          <div className={`${cn.loadingError} ${cn.normalWidth}`}>
            <div className={cn.loadingErrorTitle}>Unable to load data</div>
            <div className={cn.loadingErrorText}>{errorList}</div>
          </div>
        )}

        {!errorList && (
          <>
            <GeneralInfo
              isLoading={!generalInfo && (isLoading || isFetching)}
              generalInfo={generalInfo}
              pspGeneralInfo={pspGeneralInfo}
              changeVisibility={setVisibleByClientsPopupIsVisible}
              countries={countries}
              accountId={accountId}
              onStatusChange={onStatusChange}
              statusChangeInProgress={enableInProgress}
              currentBalances={currentBalances}
              visiblityChangeInProgress={visiblityChangeInProgress}
              overdraftEnabled={overdraftEnabled}
              auditLogAvailable={activePermissions?.includes(
                PermissionEnum.AUDIT_LOG_LIST,
              )}
              casp={casp?.map((item) => ({ name: item.name, value: item.id }))}
              changeExclude={setExcludeForExcternalPopupVisible}
              setBindSidepanelIsOpen={setBindSidepanelIsOpen}
              changeDefaultState={setUseAsDefaultPopupVisible}
              defaultStateChangeInProgress={defaultStateChangeInProgress}
              search={search}
            />
            {activePermissions?.includes(
              PermissionEnum.PSP_ACCOUNT_DOCUMENTS_LIST,
            ) && (
              <Documents
                isLoading={documentsIsLoading || documentsIsFetching}
                documents={documents}
                accountId={accountId}
                isEditable={activePermissions?.includes(
                  PermissionEnum.PSP_ACCOUNT_DOCUMENTS_CREATE_ACTION,
                )}
                accountPage
                search={search}
              />
            )}
            <InternalAccounts
              isLoading={
                internalAccountsIsLoading ||
                internalAccountsIsFetching ||
                isLoading ||
                isFetching
              }
              internalAccounts={internalAccounts}
              internalAccountsErrors={internalAccountsErrors}
              balances={balances}
            />
            <ChangeLog
              isLoading={logsIsLoading || logsIsFetching}
              changeLog={changeLog}
              logsErrors={logsErrors}
            />
          </>
        )}
        <Popup
          isVisible={bindSidepanelIsOpen}
          title={`Link ${
            pspGeneralInfo?.pspIntegration?.name === 'PRIME_VAULT_WALLET'
              ? 'Vault'
              : 'External Account'
          }`}
          onCancelClick={() => {
            if (isDirty) {
              setDirtyPopupVisible(true);
            } else {
              setBindSidepanelIsOpen(false);
              setVaultSetting('bind');
              setIbanValue(generalInfo?.realNumberOrIban as string);
            }
          }}
          onOkClick={onLink}
          cancelButtonText="Close"
          okButtonText={linkButtonText}
          okButtonDisabled={
            updateIbanInProgress || bindInProgress || createNewInProgress
          }
        >
          <div className={cn.externalVaultSettings}>
            <div className={cn.externalVaultSettingsTitle}>
              External&nbsp;
              {pspGeneralInfo?.pspIntegration?.name === 'PRIME_VAULT_WALLET'
                ? 'Vault'
                : 'Account'}
              &nbsp; settings
            </div>
            <div className={cn.externalVaultSettingsValues}>
              <div className={cn.externalVaultSettingsValue}>
                <Checkbox
                  name="bind"
                  checked={Boolean(vaultSetting === 'bind')}
                  type="radio"
                  onChange={() => setVaultSetting('bind')}
                  title="Link to existing"
                />
              </div>
              <div className={cn.externalVaultSettingsValue}>
                <Checkbox
                  name="create-new"
                  checked={Boolean(vaultSetting === 'create-new')}
                  type="radio"
                  onChange={() => {
                    setVaultSetting('create-new');
                    if (ibanValue !== generalInfo?.realNumberOrIban) {
                      setIbanValue(generalInfo?.realNumberOrIban as string);
                    }
                  }}
                  title="Create new"
                />
              </div>
            </div>
            {vaultSetting === 'bind' && (
              <InputText
                name="realNumberOrIban"
                placeholder={`Enter external ${pspGeneralInfo?.pspIntegration?.name === 'PRIME_VAULT_WALLET' ? 'vault' : 'account'} number`}
                onChange={(e) => setIbanValue(e.target.value)}
                value={ibanValue}
                title={`${pspGeneralInfo?.pspIntegration?.name === 'PRIME_VAULT_WALLET' ? 'Vault' : 'External Account'} number (Real Account Number)`}
                error={errors?.realNumberOrIban?.message as string}
              />
            )}
            {vaultSetting === 'create-new' && (
              <div className={cn.automaticallyCreateDescription}>
                External Vault will be created and linked to this account
                automatically
              </div>
            )}
            {generalBindError && (
              <div className={cn.bindAccountError}>{generalBindError}</div>
            )}
          </div>
        </Popup>
        <Popup
          title="You have unsaved changes that will be lost"
          description="Are you sure you want to close this form?"
          onCancelClick={() => setDirtyPopupVisible(false)}
          onOkClick={() => {
            setBindSidepanelIsOpen(false);
            setVaultSetting('bind');
            setIbanValue(generalInfo?.realNumberOrIban as string);
            setDirtyPopupVisible(false);
          }}
          cancelButtonText="Cancel"
          okButtonText="Close"
          isVisible={dirtyPopupVisible}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        <Popup
          title={
            generalInfo?.visibleToClient
              ? 'Hide account from the client?'
              : 'Make account visible to the clients?'
          }
          description={
            generalInfo?.visibleToClient
              ? `The clients will no longer see account ${generalInfo?.account?.fullNumber} or its balance in online banking. Existing operations on the account will continue as usual.`
              : `Account ${generalInfo?.account?.fullNumber}, its balance and transaction history will appear in clients online banking.`
          }
          onCancelClick={() => setVisibleByClientsPopupIsVisible(false)}
          onOkClick={() => {
            changeVisibility();
          }}
          cancelButtonText="Cancel"
          okButtonText={
            // eslint-disable-next-line no-nested-ternary
            generalInfo?.visibleToClient
              ? visiblityChangeInProgress
                ? 'Loading...'
                : 'Hide account'
              : visiblityChangeInProgress
                ? 'Loading...'
                : 'Make visible'
          }
          isVisible={visibileByClientsPopupIsVisible}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        <Popup
          title={
            generalInfo?.excludeFromTopUpIfExternalAccountAvailable
              ? 'Include account in top-up?'
              : 'Exclude account from top-up?'
          }
          description={
            generalInfo?.excludeFromTopUpIfExternalAccountAvailable
              ? 'This account will be offered for top-up even when an external account is available.'
              : `Account ${generalInfo?.account?.fullNumber}, its balance and transaction history will appear in clients online banking.`
          }
          onCancelClick={() => setExcludeForExcternalPopupVisible(false)}
          onOkClick={() => {
            changeExclude();
          }}
          cancelButtonText="Cancel"
          okButtonText={
            // eslint-disable-next-line no-nested-ternary
            generalInfo?.excludeFromTopUpIfExternalAccountAvailable
              ? visiblityChangeInProgress
                ? 'Loading...'
                : 'Include'
              : visiblityChangeInProgress
                ? 'Loading...'
                : 'Exclude'
          }
          isVisible={excludeForExcternalPopupVisible}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        <Popup
          title={
            generalInfo?.isDefaultForExternalOperations
              ? 'Remove default status?'
              : 'Set as default for external operations?'
          }
          description={
            generalInfo?.isDefaultForExternalOperations
              ? 'The client will have no default account for external operations and will need to select one manually for each operation.'
              : 'External operations will use this account by default'
          }
          onCancelClick={() => setUseAsDefaultPopupVisible(false)}
          onOkClick={() => {
            changeDefaultStateAction();
          }}
          cancelButtonText="Cancel"
          okButtonText={
            // eslint-disable-next-line no-nested-ternary
            generalInfo?.isDefaultForExternalOperations
              ? defaultStateChangeInProgress
                ? 'Loading...'
                : 'Remove default'
              : defaultStateChangeInProgress
                ? 'Loading...'
                : 'Set as default'
          }
          isVisible={useAsDefaultPopupVsibile}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        {createNewHintVisible && (
          <div className={cn.reportNotification}>
            <div className={cn.reportNotificationLeft}>
              <IconLoader />
              <div className={cn.reportText}>Account will be created soon</div>
            </div>
          </div>
        )}
        <div id="dirty-popup" style={{ zIndex: 100 }} />
        <Outlet />
      </div>
    </div>
  );
};

export default AccountPage;
